<template>
  <div class="animated" v-if="results && results.models.length">
    <b-row class="my-3" align-h="end">
      <b-col class="pl-1 pr-0" cols="3">
        <b-button v-if="hasCompletion" @click="completeAll" block variant="dark"
          >Complete All</b-button
        >
      </b-col>
      <b-col cols="3">
        <b-button block @click="saveAll" variant="success"
          ><i class="fal fa-save" /> Submit All</b-button
        >
      </b-col>
    </b-row>
    <b-table
      :items="results.models"
      :fields="columns"
      responsive
      striped
      hover
      small
    >
      <template #cell(sample_receiving_id)="data">
        <router-link :to="`/samples/${data.item.sample.id}`">
          {{ data.item.sample.receiving_id }}
        </router-link>
      </template>
      <template #cell(project_name)="data">
        <router-link :to="`/projects/${getProjectData(data.item, 'id')}`">
          {{ getProjectData(data.item, "pid") }}
        </router-link>
      </template>
      <template #cell(customer_name)="data">
        <router-link :to="`/customer/${getCustomerData(data.item, 'id')}`">
          {{ getCustomerData(data.item, "name") }}
        </router-link>
      </template>
      <template #cell(raw_result)="data">
        <b-form-radio-group
          v-if="data.item.analysis && data.item.analysis.name === 'Completion'"
          v-model="data.item.raw_result"
          :options="[
            { text: 'In Progress', value: null },
            { text: 'Done', value: 1 }
          ]"
          buttons
          button-variant="outline-dark"
          size="md"
          name="radio-btn-outline"
        />
        <b-input
          v-else
          type="number"
          v-model.number="data.item.raw_result"
        ></b-input>
      </template>
      <template #cell(formatted_result)="data">
        <b-input
          v-if="
            !(data.item.analysis && data.item.analysis.name === 'Completion')
          "
          type="text"
          v-model="data.item.formatted_result"
        />
        <b-alert v-else class="py-2 m-0" show
          >No Formatted Value Required</b-alert
        >
      </template>
      <template #cell(unit_of_measure_id)="data">
        <b-select
          v-model="data.item.unit_of_measure_id"
          v-if="picked_analysis && picked_analysis.units.length"
        >
          <b-select-option
            v-for="unit in picked_analysis.units"
            :value="unit.id"
            :key="unit.id"
            >{{ unit.abbrev }}</b-select-option
          >
        </b-select>
        <b-alert v-else class="py-2 m-0" show>No Analysis Units</b-alert>
      </template>
      <template #cell(actions)="data">
        <b-button
          class="mr-2"
          variant="dark"
          v-b-tooltip.html
          v-b-modal="`result-history-${data.item._uid}`"
          title="View History"
          ><i class="fal fa-history"
        /></b-button>
        <b-button
          variant="success"
          v-b-tooltip.html
          title="Save Result"
          @click="save(data.item)"
          ><i class="fal fa-save"
        /></b-button>
        <schedule-result-history-model
          :id="`result-history-${data.item._uid}`"
          :analysis="data.item.analysis"
          :sample="data.item.sample"
          :instrument_instance="data.item.instrument_instance"
        />
      </template>
    </b-table>
  </div>
</template>

<script>
import ScheduleResultHistoryModel from "./ScheduleResultHistoryModal";
import AnalyteResultCollection from "@/collections/lab/AnalyteResultCollection";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";

import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";

const AnalyteResultRepository = LabRepositoryFactory.get("analyteResult");

export default {
  name: "ScheduleResultsTable",
  components: { ScheduleResultHistoryModel },
  mixins: [ToastNotifications],
  props: {
    results: {
      type: AnalyteResultCollection,
      default: () => null
    },
    picked_analysis: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      columns: [
        { key: "sample_receiving_id", label: "Sample" },
        { key: "analyte.name", label: "Analyte" },
        { key: "customer_name", label: "Customer" },
        { key: "project_name", label: "Project" },
        { key: "raw_result", label: "Raw" },
        { key: "formatted_result", label: "Formatted" },
        { key: "unit_of_measure_id", label: "Unit" },
        { key: "actions", label: "Actions" }
      ],
      units: null,
      show_modal: {}
    };
  },
  computed: {
    hasCompletion() {
      return (
        this.results &&
        this.results.models &&
        this.results.models.length &&
        this.results.models.filter(i => {
          return i.analysis && i.analysis.name === "Completion";
        }).length
      );
    }
  },
  methods: {
    getSubmissionData(item) {
      return item.sample.submission && item.getSample().getSubmission();
    },
    getProjectData(item, data) {
      const submission = this.getSubmissionData(item);
      const project = submission.project && submission.getProject();

      return project && (data === "id" ? project.id : project.publicNumber());
    },
    getCustomerData(item, data) {
      const submission = this.getSubmissionData(item);
      return (
        submission.project &&
        submission.project.customer &&
        submission.project.customer[data]
      );
    },
    async save(result) {
      const payload = result.cloneAttributes(true, true, [
        "created_at",
        "created_by",
        "deleted_at",
        "updated_by",
        "updated_at",
        "id"
      ]);

      try {
        await AnalyteResultRepository.create(payload);

        this.quickSuccessToast(
          "Result Saved",
          "The pending result was saved. View the history to approve the result."
        );
      } catch ({ response }) {
        const { status, data } = response;

        this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
          title: "Result Save Error.",
          variant: "danger"
        });
      }
    },
    completeAll() {
      this.results.models.forEach(i => {
        if (i.analysis && i.analysis.name === "Completion") {
          i.raw_result = 1;
        }
      });
    },
    saveAll() {
      this.results.models.forEach(i => {
        if (i.raw_result !== null && typeof i.raw_result !== "undefined") {
          this.save(i);
        }
      });
    }
  }
};
</script>
